import Head from 'next/head';
import Link from 'next/link';
import type { GetStaticPaths, GetStaticProps } from 'next';

import WideLayout from '@components/layout/wide';
import { FFH_HOSTNAME } from '@lib/constants';
import { resolveClub } from '@lib/page-utils/player-profile/catalogue';
import {
  buildBreadcrumbJsonLd,
  type BreadcrumbEntry
} from '@lib/page-utils/player-profile/seo';
import {
  buildPlayerSlug,
  PLAYERS_INDEX_PATH,
  toClubIndexPath,
  toPlayerProfilePath
} from '@lib/page-utils/player-profile/slugs';
import {
  formatOwnership,
  formatPrice
} from '@lib/page-utils/player-profile/format';

const BRAND_NAME = 'Fantasy Football Hub';
const CLUB_REVALIDATE_SECONDS = 3600;
const NOT_FOUND_REVALIDATE_SECONDS = 3600;

type SquadRow = {
  displayName: string;
  position: string;
  price: number | null;
  ownership: number | null;
  points: number;
  path: string;
};

type ClubIndexProps = {
  teamName: string;
  clubSlug: string;
  squad: SquadRow[];
};

const ClubIndexPage = ({ teamName, clubSlug, squad }: ClubIndexProps) => {
  const canonicalUrl = `${FFH_HOSTNAME}${toClubIndexPath(clubSlug)}`;
  const title = `${teamName} FPL players`;
  const description = `Every ${teamName} player in Fantasy Premier League: prices, ownership and points, with full stat profiles covering form, expected goals and fixtures.`;

  const breadcrumbs: BreadcrumbEntry[] = [
    { label: 'Toolbox', path: '/tools' },
    { label: 'Player profiles', path: PLAYERS_INDEX_PATH },
    { label: teamName }
  ];

  return (
    <>
      <Head>
        <title>{`${title} - prices, points and profiles | ${BRAND_NAME}`}</title>
        <meta name="description" content={description} key="description" />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:title" content={title} key="og:title" />
        <meta
          property="og:description"
          content={description}
          key="og:description"
        />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        <meta property="og:site_name" content={BRAND_NAME} key="og:site_name" />
        <script
          type="application/ld+json"
          key="jsonld-breadcrumbs"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildBreadcrumbJsonLd(breadcrumbs))
          }}
        />
      </Head>

      <WideLayout content={false}>
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
          <nav
            aria-label="Breadcrumb"
            className="mb-4 text-xs text-neutral-500"
          >
            <Link href="/tools" className="hover:underline">
              Toolbox
            </Link>
            <span aria-hidden className="mx-2 text-neutral-300">
              /
            </span>
            <Link href={PLAYERS_INDEX_PATH} className="hover:underline">
              Player profiles
            </Link>
            <span aria-hidden className="mx-2 text-neutral-300">
              /
            </span>
            <span className="text-neutral-800">{teamName}</span>
          </nav>

          <h1 className="font-sans text-3xl font-bold text-neutral-900">
            {teamName} FPL players
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
            Pick a player for their full FPL profile: form against expected
            points, underlying OPTA numbers, upcoming fixtures and a
            gameweek-by-gameweek log.
          </p>

          <ul className="mt-6 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200 bg-white">
            {squad.map((row) => (
              <li key={row.path}>
                <Link
                  href={row.path}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-neutral-50 sm:px-5"
                >
                  <span className="w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                    {row.position}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900">
                    {row.displayName}
                  </span>
                  <span className="hidden w-16 text-right text-sm tabular-nums text-neutral-600 sm:block">
                    {formatOwnership(row.ownership)}
                  </span>
                  <span className="w-14 text-right text-sm tabular-nums text-neutral-600">
                    {formatPrice(row.price)}
                  </span>
                  <span className="w-14 text-right text-sm font-semibold tabular-nums text-neutral-900">
                    {row.points} pts
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {squad.length === 0 && (
            <p className="mt-6 text-sm text-neutral-500">
              No players are listed for {teamName} right now. Squads refresh
              through the day, so check back shortly.
            </p>
          )}
        </div>
      </WideLayout>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});

export const getStaticProps: GetStaticProps<ClubIndexProps> = async ({
  params
}) => {
  const clubSlug = typeof params?.club === 'string' ? params.club : null;
  if (!clubSlug) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const club = await resolveClub(clubSlug);
  if (!club) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const squad = club.squad.map((player) => ({
    displayName: player.displayName,
    position: player.position,
    price: player.price ?? null,
    ownership: player.ownership ?? null,
    points: player.seasonStats?.points?.total ?? 0,
    path: toPlayerProfilePath(
      club.clubSlug,
      buildPlayerSlug(player, club.squad)
    )
  }));

  return {
    props: { teamName: club.team.name, clubSlug: club.clubSlug, squad },
    revalidate: CLUB_REVALIDATE_SECONDS
  };
};

export default ClubIndexPage;
